import type { ReactNode } from 'react';
import './process-steps.css';

export type ProcessStepItem = {
  num: string;
  title: string;
  description?: string;
  hint?: string;
  sub?: string;
  icon: ReactNode;
};

type Props = {
  steps: ProcessStepItem[];
  columns?: 3 | 5;
  activeStep?: number;
  onSelect?: (step: number) => void;
  ariaLabel: string;
  className?: string;
  surface?: 'default' | 'none';
};

export function ProcessStepsInfographic({
  steps,
  columns = 3,
  activeStep,
  onSelect,
  ariaLabel,
  className = '',
  surface = 'default',
}: Props) {
  const interactive = Boolean(onSelect);
  const Tag = interactive ? 'div' : 'ol';

  return (
    <Tag
      className={`process-steps process-steps--${columns} process-steps--${interactive ? 'interactive' : 'static'}${surface === 'none' ? ' process-steps--surface-none' : ''} ${className}`.trim()}
      role={interactive ? 'tablist' : undefined}
      aria-label={ariaLabel}
    >
      {steps.map((def, index) => {
        const stepNum = index + 1;
        const active = activeStep === stepNum;
        const done = activeStep !== undefined && stepNum < activeStep;
        const stepClass = `process-steps-step process-steps-step--${stepNum}${active ? ' is-active' : ''}${done ? ' is-done' : ''}`;

        const cardContent = (
          <>
            <div className="process-steps-header" aria-hidden />
            <span className="process-steps-num">{def.num}</span>
            <span className="process-steps-title">{def.title}</span>
            {def.description ? <p className="process-steps-desc">{def.description}</p> : null}
            {def.sub ? <span className="process-steps-sub">{def.sub}</span> : null}
            {def.hint && !def.sub ? <span className="process-steps-hint">{def.hint}</span> : null}
          </>
        );

        const inner = interactive ? (
          <button
            type="button"
            role="tab"
            aria-selected={active}
            className={`process-steps-card${active ? ' is-active' : ''}${done ? ' is-done' : ''}`}
            onClick={() => onSelect?.(stepNum)}
          >
            {cardContent}
          </button>
        ) : (
          <div className="process-steps-card">{cardContent}</div>
        );

        return interactive ? (
          <div key={def.num} className={stepClass}>
            <div className="process-steps-icon">{def.icon}</div>
            {inner}
          </div>
        ) : (
          <li key={def.num} className={stepClass}>
            <div className="process-steps-icon">{def.icon}</div>
            {inner}
          </li>
        );
      })}
    </Tag>
  );
}
